import React, { useCallback } from 'react';
import Modal from '@monofront/design-system/organisms/Modal';
import {
  Button,
  ErrorText,
  Input,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { useMutation } from '@apollo/client';
import {
  SUBSCRIBE_TO_FRESH_WAITLIST,
  SUBSCRIBE_TO_ULTRA_FRESH_WAITLIST,
} from '@monofront/business/queries';
import { EMAIL_REGEX } from '@monofront/business/constants';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import { getIsFresh, getIsUltraFresh } from '@monofront/business/utils';
import { machineSelectedAddressSelector } from '@monofront/business/stateMachines/deliveryBar/selectors';
import { toast } from 'react-toastify';
import { useDeliveryBar } from '@context/DeliveryBarContext';

const SubscribeToWaitingList: React.FC<{
  isOpen: boolean;
  handleCloseModal: () => void;
}> = ({ isOpen, handleCloseModal }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    formState: { dirtyFields, touchedFields },
    getValues,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  });

  const { waitingProduct } = useDeliveryBar();
  const [addToFreshWaitingList] = useMutation(SUBSCRIBE_TO_FRESH_WAITLIST);
  const [addToUltraFreshWaitingList] = useMutation(
    SUBSCRIBE_TO_ULTRA_FRESH_WAITLIST,
  );
  const isValidEmail =
    (Boolean(getValues().email) || 'email' in dirtyFields) && !errors.email;

  const address = DeliveryBarMachineContext.useSelector(
    machineSelectedAddressSelector,
  );

  const tSubscribeToWaitingList = useTranslations('subscribeToWaitingList');
  const tAccount = useTranslations('Account');

  const handleAddToWaitingList = useCallback(
    async (data) => {
      if (getIsFresh(waitingProduct?.tags || [])) {
        try {
          await addToFreshWaitingList({
            variables: {
              email: data.email,
              postalCode: address?.postalCode || '',
              street: address?.street || '',
              city: address?.city || '',
              country: address?.country || '',
            },
          });

          toast(tSubscribeToWaitingList('successMessage'));
        } catch (error) {
          toast(tSubscribeToWaitingList('errorMessage'), {
            progressClassName: 'toast-error',
          });
        }
      } else if (getIsUltraFresh(waitingProduct?.tags || [])) {
        try {
          await addToUltraFreshWaitingList({
            variables: {
              email: data.email,
              postalCode: address?.postalCode || '',
              street: address?.street || '',
              city: address?.city || '',
              country: address?.country || '',
            },
          });

          toast(tSubscribeToWaitingList('successMessage'));
        } catch (error) {
          toast(tSubscribeToWaitingList('errorMessage'), {
            progressClassName: 'toast-error',
          });
        }
      }

      handleCloseModal();
    },
    [address],
  );

  const handleClose = () => {
    handleCloseModal();
  };

  return (
    <>
      <Modal
        title={tSubscribeToWaitingList('title')}
        isOpen={isOpen}
        externalToggle={handleClose}
        UNSAFE_className="tw-px-10 tw-py-8 tw-w-[390px] tw-flex tw-flex-col tw-gap-3"
        isClosable
        centered
      >
        <form onSubmit={handleSubmit(handleAddToWaitingList)}>
          <div
            className="tw-flex tw-flex-col tw-items-center tw-gap-3"
            data-testname="email form inputs"
          >
            <TextInput
              id="email"
              isValid={isValidEmail}
              isInvalid={!!touchedFields?.email && !!errors.email}
              UNSAFE_className="tw-w-full"
            >
              <Input
                type="email"
                autoComplete="email"
                placeholder={tAccount('yourEmail')}
                {...register('email', {
                  required: true,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: tAccount('useValidEmail'),
                  },
                })}
              />
              {errors.email && (errors.email.message as string) && (
                <ErrorText dataTestId="">
                  {errors.email.message as string}
                </ErrorText>
              )}
            </TextInput>
            <Button className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-mx-auto tw-mb-4">
              {tSubscribeToWaitingList('submitButton')}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default SubscribeToWaitingList;
